/**
 * Source-kind routing for the builder's two backend surfaces (RFC 062).
 *
 * OpenSearch datasets keep the classic /api/v2/search endpoints; SQL
 * datasets (slug registered via registerSqlDatasets) route to
 * /api/v2/sql-search with the same DSL body and get the same OpenSearch
 * response envelope back — the single deliberate seam the RFC allows.
 */
import type { RequestInfo } from '@warp-drive/core/types/request';
import { executeSearchRequest, getSearchFieldsRequest } from 'tio-data/requests/search';
import { sqlSearchFieldsRequest, sqlSearchRequest } from 'tio-data/requests/sql-dataset';
import { isSqlDataset } from './dataset-utils';

export function fieldsRequestFor(
  modelType: string,
  companyId?: string,
  programTemplateId?: string
): RequestInfo {
  if (isSqlDataset(modelType)) {
    return sqlSearchFieldsRequest(modelType);
  }
  return getSearchFieldsRequest(modelType, companyId, programTemplateId);
}

/**
 * For SQL datasets, tenant scoping is a request param (never a client
 * filter): param-kind datasets require companyId — callers gate on it
 * before querying — while cross-company-capable (column-kind) datasets
 * may omit it, letting the backend authorize an unscoped query. For
 * OpenSearch datasets the company filter already lives inside the search
 * body and companyId is ignored.
 */
export function searchRequestFor(query: Record<string, unknown>, companyId?: string): RequestInfo {
  const modelType = (query['model-type'] ?? query['modelType'] ?? query['model_type']) as
    string | undefined;

  if (modelType && isSqlDataset(modelType)) {
    return sqlSearchRequest({
      dataset: modelType,
      companyId,
      searchBody: (query['searchBody'] ?? query['search_body'] ?? {}) as Record<string, unknown>,
    });
  }
  return executeSearchRequest(query);
}
